import React, { FunctionComponent, useEffect, useState } from 'react';
import { useSwipeable } from 'react-swipeable';
import { FormattedMessage } from 'react-intl';
import { map, difference, trim } from 'ramda';
import { IBindingCallback1, IBindingAction } from '../../common/models/callback';
import { ReferenceType } from '../../common/enums/ReferenceType';
import { IRefReqCounters } from '../../common/models/referenceRequest/IRefReqCounters';
import { IReferenceCandidateForm } from '../../common/models/reference/IReferenceCandidateForm';
import { refTypes, getTotalByType, messages, getCountByType } from './utils';
import RegularButton from '../RegularButton';
import Input from '../Input';
import IconButton from '../IconButton';
import { ReactComponent as ArrowRight } from '../../assets/icons/arrow-right.svg';
import { ReactComponent as ArrowLeft } from '../../assets/icons/arrow-left.svg';
import { handleEnter } from '../../common/helpers/keyboardHelper';
import styles from './styles.module.scss';

interface IReferenceRoleFormProps {
  references: IReferenceCandidateForm[];
  counters: IRefReqCounters;
  disabledTypes: ReferenceType[];
  currentReference: IReferenceCandidateForm;
  onGoBack: IBindingAction;
  onContinue: IBindingCallback1<{ type: ReferenceType; otherType?: string }>;
}

export const ReferenceRoleForm: FunctionComponent<IReferenceRoleFormProps> = ({
  references,
  counters,
  disabledTypes,
  currentReference,
  onGoBack,
  onContinue
}) => {
  const [referenceType, setReferenceType] = useState<ReferenceType | undefined>(currentReference.type || undefined);
  const [otherType, setOtherType] = useState<string>(currentReference.otherType || '');
  const isDisabled = referenceType === ReferenceType.Other ? !trim(otherType) : !referenceType;

  const onSelect = () => onContinue({
    type: referenceType as ReferenceType,
    otherType: referenceType === ReferenceType.Other ? trim(otherType) : undefined
  });

  const keyHandler = (event: KeyboardEvent) => handleEnter(event, () => {
    if (!isDisabled) {
      onSelect();
    }
  });

  useEffect(() => {
    window.addEventListener('keydown', keyHandler);
    return () => {
      window.removeEventListener('keydown', keyHandler);
    };
  }, [keyHandler]);

  const swipeHandler = useSwipeable({
    onSwipedLeft: () => {
      if (!isDisabled) onSelect();
    },
    onSwipedRight: onGoBack,
    trackMouse: false
  });

  return (
    <div {...swipeHandler} className="cf-container">
      <div className="cf-header-title mb-2">
        <FormattedMessage id="reference" defaultMessage="Reference" />
        &nbsp;
        {currentReference.id}
      </div>
      <div className="cf-title mb-4">
        <FormattedMessage
          id="candidateForm.relationship.title"
          defaultMessage="What is your relationship to {firstName}?"
          values={{ firstName: currentReference.firstName }}
        />
      </div>
      <div className={styles.optionsContainer}>
        {counters && map(type => {
          const selected = referenceType === type;
          const countByType = getCountByType(references, type);
          const totalByType = getTotalByType(type, counters);
          return (
            <div
              key={type}
              role="presentation"
              className={`${selected ? styles.selected : ''} ${styles.item} cursor-pointer`}
              onClick={() => setReferenceType(type)}
            >
              <div className="d-flex align-items-center">
                <div className={styles.icon}>{selected && <span />}</div>
                &nbsp;
                <FormattedMessage {...messages[type]} />
              </div>
              {Boolean(totalByType || countByType) && (
                <div className={styles.counters}>
                  {countByType}
                  &nbsp;
                  <FormattedMessage id="of" />
                  &nbsp;
                  {totalByType}
                </div>
              )}
            </div>
          );
        }, difference(refTypes, disabledTypes) as ReferenceType[])}
        {referenceType === ReferenceType.Other ? (
          <Input
            autoFocus
            type="text"
            label={{
              id: 'candidateForm.relationship.other',
              defaultMessage: 'Please specify the reference’s relationship to you'
            }}
            value={otherType}
            onChange={setOtherType}
            variant="underlined"
            className="mt-3"
            size="lg"
          />
        )
          : <></>}
      </div>
      <div className="cf-footer-btns">
        <IconButton
          size="lg"
          icon={<ArrowLeft />}
          onClick={onGoBack}
        />
        <RegularButton
          disabled={isDisabled}
          size="lg"
          textIntlId="candidateForm.next"
          defaultMessage="Next"
          icon={<ArrowRight />}
          iconPosition="right"
          onClick={onSelect}
        />
      </div>
    </div>
  );
};

export default ReferenceRoleForm;
